// Tiny, dependency-free charts for the admin reporting screens. The app ships no
// charting library (and no build framework beyond Vite), so these are hand-rolled
// SVG/CSS — enough for simple time-series bars, a line, and a distribution. They
// inherit the theme tokens and stay crisp at any width.

import { useId } from 'react'

export interface Point {
  label: string
  value: number
  /** Optional richer tooltip; falls back to `${label}: ${value}`. */
  title?: string
}

const niceMax = (max: number) => (max <= 0 ? 1 : max)

// ── vertical bars (signups, churn count, activity/day) ──────────────────────
export function BarChart({
  points,
  height = 120,
  color = 'var(--c-accent)',
  format = (n: number) => String(n),
}: {
  points: Point[]
  height?: number
  color?: string
  format?: (n: number) => string
}) {
  const max = niceMax(Math.max(...points.map((p) => p.value), 0))
  return (
    <div className="chart-bars" style={{ height }}>
      {points.map((p, i) => (
        <div key={i} className="chart-bars__col" title={p.title ?? `${p.label}: ${format(p.value)}`}>
          <div className="chart-bars__track">
            <div
              className="chart-bars__bar"
              style={{ height: `${(p.value / max) * 100}%`, background: color }}
            />
          </div>
          <span className="chart-bars__label">{p.label}</span>
        </div>
      ))}
    </div>
  )
}

// ── line (MRR over time) ────────────────────────────────────────────────────
export function LineChart({
  points,
  height = 140,
  color = 'var(--cred-live)',
  format = (n: number) => String(n),
}: {
  points: Point[]
  height?: number
  color?: string
  format?: (n: number) => string
}) {
  const gid = useId()
  const w = 100
  const h = 100
  const max = niceMax(Math.max(...points.map((p) => p.value), 0))
  const n = Math.max(points.length - 1, 1)
  const xy = points.map((p, i) => [(i / n) * w, h - (p.value / max) * h] as const)
  const line = xy.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
  const area = `0,${h} ${line} ${w},${h}`
  return (
    <div className="chart-line" style={{ height }}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="chart-line__svg">
        <defs>
          <linearGradient id={`fill-${gid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#fill-${gid})`} />
        <polyline points={line} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="chart-line__axis">
        {points.map((p, i) => (
          <span key={i} title={`${p.label}: ${format(p.value)}`}>
            {i % 2 === 0 ? p.label : ''}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── horizontal distribution (plan mix) ──────────────────────────────────────
export function DistBars({
  rows,
}: {
  rows: { label: string; value: number; sub?: string; color?: string }[]
}) {
  const max = niceMax(Math.max(...rows.map((r) => r.value), 0))
  return (
    <div className="chart-dist">
      {rows.map((r, i) => (
        <div key={i} className="chart-dist__row">
          <span className="chart-dist__label">{r.label}</span>
          <div className="chart-dist__track">
            <div
              className="chart-dist__bar"
              style={{ width: `${(r.value / max) * 100}%`, background: r.color ?? 'var(--c-accent)' }}
            />
          </div>
          <span className="chart-dist__value tnum">
            {r.value}
            {r.sub ? <small> · {r.sub}</small> : null}
          </span>
        </div>
      ))}
    </div>
  )
}
